import { computed, shallowRef, ref } from 'vue';
import { cloneDeep, forEach, get, isEmpty, merge, omit, findIndex, map } from 'lodash';

function getFirstError(header, row, newValue) {
  const allRulesInHeader = {
    valueRules: get(header, 'rules', []),
    serverRules: get(header, 'serverRules', []),
  };
  // only compute one error at a time as the user can only see one
  let firstError = null;
  forEach(allRulesInHeader, (rules, ruleType) => {
    forEach(rules, rule => {
      const params = ruleType === 'valueRules' ? newValue : { ...row, [header.value]: newValue };
      const isValidOrErrorMessage = rule(params);
      if (isValidOrErrorMessage !== true) {
        firstError = isValidOrErrorMessage;
        // no need to continue loop, so stop on first error
        return false;
      }
    });
  });

  return firstError;
}

/*
 * The idea behind this utility file is to create a generic store that can be used in any store module that uses editable tables on the FE component.
 * This "module" will then enhance existing stores to handle updates on cell contents, run local validations, etc.
 * Given that a FE table needs to fetch/update/discard based on a store data, the component should be using directly the content from the getter `getTableRows` to build the tablee.
 * To make it work as expected, the method `initialiseEditableTable` should be called from the Vue component before rendering.
 *
 * For details on integration on:
 *  - Vue component: see `furniture-editor.vue`,
 *  - Store module: see `store/modules/furniture.js`
 * */

export function useEditableTableStore() {
  /* represents a getter from the original store to retrieve the original data.
    This is required to compare if any changes were made.
    */
  const localDataGetterName = shallowRef('');
  /* Represents the changed value per cell. See setCellChanges for more. It would append the updates here as:
  localDataChangesPerCell: {
    // index of data in table, it will probably be replaced to use dynamic ids depending on the outcome of AOV3-632/661
    0: {
      [propertyThatChanged]: [newPropertyValue],
      size: 1000.7,
    },
  }
  */
  const localDataChangesPerCell = ref({});
  // Represents the data that will feed the table inside the Vue template
  const localTableRowContents = ref([]);
  /* Similarly to the changes object above, but the value for each entry is an message that will feed the triangle error component.
      0: {
        [propertyThatChanged]: "Value should be alphanumeric",
        size: "Size must be a number",
      },
    */
  const localInvalidErrors = ref({});

  // Getters
  const getTableRows = computed(() =>
    map(localTableRowContents.value, (el, idx) => ({ ...el, index: idx }))
  );
  const hasInvalidValuesInAnyCell = computed(() => {
    return !isEmpty(localInvalidErrors.value);
  });
  const userMadeChangesToAnyCell = computed(() => {
    return !isEmpty(localDataChangesPerCell.value);
  });
  const hasDataIn = computed(() => (field, header, rowIndex) => {
    const rowChanges = this[field].vlaue[rowIndex];
    if (!rowChanges) return false;

    return !!rowChanges[header.value];
  });
  const hasChangesInCell = computed(() => (header, rowIndex) => {
    return hasDataIn.value('localDataChangesPerCell', header, rowIndex);
  });
  const hasInvalidDataInCell = computed(() => (header, rowIndex) => {
    return hasDataIn.value('localInvalidErrors', header, rowIndex);
  });

  // Previous mutations
  function setNameOfLocalGetter(path) {
    localDataGetterName.value = path;
  }
  function setTableRows(newTableContents) {
    localTableRowContents.value = newTableContents;
  }
  function setInputValue({ row, newValue, header, rowIndex }) {
    const updatedRow = { ...row, [header.value]: newValue };
    localTableRowContents.value[rowIndex] = updatedRow;
  }
  function setInputError({ header, error, rowIndex }) {
    /*
     * Compute or remove errors in cells.
     * A) If an error is passed to this mutation, then we append to the existing row entry for errors
     * B) If no error was passed, we remove this error from the existing row entry.
     * C) If after the error is removed from the row, there is no error for the current row, then remove any reference from this row.
     *
     * */
    const existingErrorsForRow = localInvalidErrors.value[rowIndex] || {};

    // Step A.
    if (error !== null) {
      existingErrorsForRow[header.value] = error;
      localInvalidErrors.value[rowIndex] = existingErrorsForRow;
    } else {
      const otherErrorsInRow = omit(existingErrorsForRow, header.value);
      // Step C.
      if (isEmpty(otherErrorsInRow)) {
        delete localInvalidErrors.value[rowIndex];
      } else {
        // Step B.
        localInvalidErrors.value[rowIndex] = otherErrorsInRow;
      }
    }
  }
  function setCellChanges({ rowIndex, newValue, header, originalData }) {
    /*
     * 1. First, we check if the new changes are the same as the original change,
     *   - 1A. if they are, then we unmark this cell as changed
     *   - 1B. if they are not, then we compute this cell as changed
     */
    const localChanges = localDataChangesPerCell.value[rowIndex] || {};
    const originalValue = get(originalData, `[${rowIndex}].[${header.value}]`);
    // Step 1.
    let updatedRowChanges = null;
    if (originalValue === newValue) {
      // Step 1A
      updatedRowChanges = omit(localChanges, header.value);
    } else {
      // Step 1B
      updatedRowChanges = merge({}, localChanges, { [header.value]: newValue });
    }
    if (isEmpty(updatedRowChanges)) {
      delete localDataChangesPerCell.value[rowIndex];
    } else {
      localDataChangesPerCell.value[rowIndex] = updatedRowChanges;
    }
  }
  /*
   * Resets the current state to its default
   * This has to be called on the `beforeRouteLeave` lifecycle hook
   * */
  function unloadData() {
    localDataGetterName.value = '';
    localDataChangesPerCell.value = {};
    localTableRowContents.value = [];
    localInvalidErrors.value = {};
  }
  function markAllRowsAsChanged() {
    // For each row, we compute the new change as changed
    const allRowsUpdated = localTableRowContents.value.reduce(
      (changes, item, index) => ({ ...changes, [index]: { ...item } }),
      {}
    );
    localDataChangesPerCell.value = allRowsUpdated;
    localInvalidErrors.value = {}; // will be populated when page re-renders
  }

  // Actions
  function initialiseEditableTable(getterNameOfOriginalData) {
    unloadData();
    setNameOfLocalGetter(getterNameOfOriginalData);
    setTableRows(cloneDeep(this[getterNameOfOriginalData]));
  }
  function onValueChanged({ header, row, value, index }) {
    // TODO: use ids on AOV3-632/661
    const rowIndex = row._id ? findIndex(localTableRowContents.value, { _id: row._id }) : index; // for uploads without ID.
    const update = { row, newValue: value, header, rowIndex };
    const firstError = getFirstError(header, row, value);
    setInputError({ ...update, error: firstError });
    setInputValue(update);
    setCellChanges({ ...update, originalData: localDataGetterName.value.value });
  }
  function setUploadedData(dataUploaded) {
    setTableRows(dataUploaded);
    markAllRowsAsChanged();
  }

  return {
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  };
}
